/** The copy of the bundles shipped with the build (`public/locales/`). */
export const LOCAL_BUNDLES = "/locales";

type RequestCallback = (
  error: unknown,
  response: { status: number; data: string } | null
) => void;

const fetchBundle = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
  const text = await response.text();
  JSON.parse(text); // an HTML fallback page (SPA rewrite) is not a bundle
  return text;
};

const fetchFirst = async (urls: string[]): Promise<string> => {
  let lastError: unknown;
  for (let i = 0; i < urls.length; i++) {
    try {
      return await fetchBundle(urls[i]);
    } catch (error) {
      lastError = error;
      if (i < urls.length - 1) {
        console.warn(`Translations unavailable at ${urls[i]}; trying ${urls[i + 1]}.`, error);
      }
    }
  }
  throw lastError;
};

/** Drops empty entries and repeats, keeping the order. */
export const bundleSources = (...baseUrls: string[]): string[] =>
  baseUrls.filter((url, index) => url && baseUrls.indexOf(url) === index);

/**
 * The `request` function for i18next-http-backend. `loadPath` points at the first base URL; when
 * that bundle cannot be read (not published yet, storage outage) the same file is tried at each
 * next base URL in turn — Glossa's published bundle, then the legacy Phrase blob, then the copy
 * shipped with the build — so a missing bundle shows older translations, never raw keys.
 */
export const createBundleLoader =
  (baseUrls: string[]) =>
  (_options: unknown, url: string, _payload: unknown, callback: RequestCallback): void => {
    const primary = baseUrls[0];
    const urls =
      primary !== undefined && url.startsWith(primary)
        ? baseUrls.map((base) => `${base}${url.slice(primary.length)}`)
        : [url];
    fetchFirst(urls)
      .then((data) => callback(null, { status: 200, data }))
      .catch((error: unknown) => callback(error, null));
  };
